import React, { useContext, useMemo } from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import { Trans } from 'react-i18next';
import {
  Avatar as BaseAvatar,
  FlexColumn,
  FlexMiddle,
  ExtraSmallSemiBold,
  semiExtraSmall,
  Tooltip as BaseTooltip,
  flexMiddle,
  smallBody,
  Flex,
  TextWithTooltip as BaseTextWithTooltip,
  FlexCenter
} from '@ubeya/shared-web/components';
import { ago } from '@ubeya/shared/utils/time';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import { API_DATE_TIME_FORMAT, LEGAL_AGE } from '@ubeya/shared/constants';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ReactComponent as BaseStarIcon } from '@ubeya/shared-web/assets/star-gold.svg';
import { ReactComponent as ApproveIconBase } from '@ubeya/shared-web/assets/tick.svg';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import useRankingBadges from '@ubeya/shared/hooks/account/useRankingBadges';
import { formatLevelOption, getEmployeeDisplayName } from '@ubeya/shared-web/utils/employee';
import { isSeniorityModelPools, isSeniorityModelRankingBased } from '@ubeya/shared/utils/accounts';
import useCanAccessSchedulingAgent from '@ubeya/shared/hooks/account/useCanAccessSchedulingAgent';
import EmployeeStatsPopover from './BookingBoard/EmployeeStatsPopover';
import MatchPill from './MatchPill';
import { ReactComponent as AiIndicatorIcon } from 'scheduling/assets/ai-indicator-dot.svg';
import { LIST_TYPE_ENUM, LIST_TYPE_COLORS } from 'scheduling/constants';
import { genderInitial, getEmployeeAgeWarning, getUserCardStatsFlags } from 'scheduling/utils';

export const USER_CARD_HEIGHT = 46;
export const USER_CARD_MARGIN_BOTTOM = 8;
export const USER_CARD_SLOT_HEIGHT = USER_CARD_HEIGHT + USER_CARD_MARGIN_BOTTOM;

const ROW_HEIGHT = 18;
const ROW_GAP = 2;
const CONTENT_HEIGHT = ROW_HEIGHT * 2 + ROW_GAP;

const contentRow = css`
  flex: 0 0 ${ROW_HEIGHT}px;
  height: ${ROW_HEIGHT}px;
  line-height: ${ROW_HEIGHT}rem;
`;

const Tooltip = styled(BaseTooltip)`
  .rc-tooltip-inner {
    max-width: 200px;
    text-align: center;
  }
`;

const ApproveIcon = styled(ApproveIconBase)`
  width: 12px;
  height: 12px;
  path {
    fill: ${({ theme }) => theme.colors.black};
  }
`;

const StarIcon = styled(BaseStarIcon)`
  height: 12px;
  width: 12px;
  margin: 0 3px;
`;

const TextWithTooltip = styled(BaseTextWithTooltip)`
  max-width: 120px;
  ${smallBody};

  overflow: hidden;
  display: inline-block !important;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Wrapper = styled(FlexMiddle)`
  ${smallBody};
  position: relative;
  height: 100%;
  max-height: ${USER_CARD_HEIGHT}px;
  min-height: ${USER_CARD_HEIGHT}px;
  padding: 0 8px 0 16px;

  &:before {
    content: '';
    position: absolute;
    border-radius: 2px 0 0 2px;
    top: 0;
    left: 0;
    bottom: 0;
    width: 8px;
    z-index: 0;
    ${({ $listType }) => $listType && `background: ${LIST_TYPE_COLORS[$listType]}`};
  }
`;

const NameTooltip = styled.div.attrs(() => ({ className: 'name' }))`
  cursor: pointer;
  font-size: 13rem;
  overflow: hidden;
  overflow-wrap: break-word;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 140px;
  word-break: break-all;
  font-weight: 500;
`;

const Warnings = styled(BaseTextWithTooltip)`
  font-size: 10rem;
  display: inline-block !important;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ListTypeInfo = styled.div`
  font-size: 10rem;
`;

const Avatar = styled(BaseAvatar)`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  align-self: center;
  /* No margin-left: the wrapper's 16px padding already clears the list-type stripe. */
  margin-right: 8px;
`;

const SeniorityRankBadge = styled.div.attrs(() => ({ className: 'seniority-rank-badge' }))`
  position: absolute;
  right: -2px;
  bottom: -2px;
  min-width: 19px;
  max-width: 28px;
  height: 12px;
  padding: 0 2px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.black};
  color: ${({ theme }) => theme.colors.white};
  font-size: 8px;
  font-weight: 600;
  line-height: 12px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Content = styled(FlexColumn).attrs(() => ({ className: 'content' }))`
  align-self: center;
  justify-content: center;
  overflow: hidden;
  flex: 1;
  height: ${CONTENT_HEIGHT}px;
  gap: ${ROW_GAP}px;
`;

const Inline = styled(Flex).attrs(() => ({ className: 'inline-data' }))`
  align-items: center;
  height: ${ROW_HEIGHT}px;
  margin-left: 15px;
`;

const Actions = styled.div``;

const Info = styled(FlexMiddle)`
  ${semiExtraSmall};
  ${contentRow};

  > div {
    margin-right: 5px;
    ${flexMiddle}
  }
  gap: 4px;
`;

const InlineTop = styled.span`
  margin-right: 3px;
  margin-left: 3px;
`;

const InfoOneLine = styled.span`
  ${semiExtraSmall};
  line-height: ${ROW_HEIGHT}rem;
  margin-right: 3px;
  margin-left: 3px;
`;

const Text = styled(ExtraSmallSemiBold)`
  font-weight: normal;
  font-size: 13rem;
  ${contentRow};
  display: inline-flex;
  align-items: center;
`;

const Hours = styled.div`
  ${({ theme, $isMarked }) => $isMarked && `color: ${theme.colors.redNegative}`};
`;

const AgeText = styled.div`
  color: ${({ theme, $showWarning }) => $showWarning && theme.colors.redNegative};
`;

const WarningContainer = styled(FlexCenter)`
  width: 15px;
  height: 15px;
  margin-right: 5px;
  border-radius: 100%;
  background-color: ${({ theme }) => theme.colors.redNegative};
`;

const UnderageText = styled.div`
  font-size: 8px;
  color: ${({ theme }) => theme.colors.white};
`;

const MatchPillWrapper = styled.div``;

// The match pill and the agent mark read as one group (Figma node 922-10741).
const MatchGroup = styled(FlexMiddle)`
  gap: 4px;
`;

const AiIndicator = styled(AiIndicatorIcon)`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
`;

export const AgeWarning = ({ isUnder18, isWarning }) => {
  if (!isUnder18 && !isWarning) return <></>;

  return (
    <WarningContainer>
      <UnderageText>{isUnder18 ? '18-' : isWarning ? 'S' : ''}</UnderageText>
    </WarningContainer>
  );
};

const UserCard = (
  {
    user: {
      listType,
      clientApproved,
      gender,
      level,
      ranking,
      scheduleNote,
      birthdate,
      createdAt,
      clientEmployeeStatus,
      firstName,
      lastName,
      isUnder18: userIsUnder18,
      isWarning: userIsWarning
    } = {},
    user,
    unbooked,
    rejected,
    createdAtPrefix = '',
    showCreatedAt = false,
    inlineActions,
    inlineData,
    inlineDataTop,
    stats = {},
    isProjectInfoLoading = false,
    isDummyEmployeeSlot = false,
    warnings,
    info,
    actions,
    shouldRenderWarningBorder,
    date,
    employeeCompliance,
    hasWarnings,
    onClick,
    openEmployeeCard,
    className,
    applicableActions,
    isExternal = false,
    isPreferredStatus = null,
    // 'booked' or 'asked' when the scheduling agent did it; undefined when a person did.
    agentAction,
    showRejected = true
  },
  ref
) => {
  /* todo, props with fallback to {} */
  const {
    dateFormat,
    timeFormat,
    isShowLastNameFirst = false,
    isBlockDemographicFiltersInSchedulingEnabled
  } = useContext(ConfigContext);
  const { t } = useContext(ConfigContext);
  const canAccessSchedulingAgent = useCanAccessSchedulingAgent();
  // A Fragment when the agent is off, so the card's DOM is exactly what it was before the mark.
  const MatchArea = canAccessSchedulingAgent ? MatchGroup : React.Fragment;
  const formattingMap = useFormattingMap();
  const { account } = useAccountConfig();
  const displayName = getEmployeeDisplayName(firstName, lastName, isShowLastNameFirst);
  const { rankingBadges } = useRankingBadges();

  const age = useMemo(
    () =>
      !isBlockDemographicFiltersInSchedulingEnabled && birthdate && moment(birthdate, API_DATE_TIME_FORMAT).isValid()
        ? moment().diff(moment(birthdate, API_DATE_TIME_FORMAT), 'years')
        : false,
    [birthdate, isBlockDemographicFiltersInSchedulingEnabled]
  );

  const { isUnder18 = false, isWarning = false } = useMemo(() => {
    if (isBlockDemographicFiltersInSchedulingEnabled) {
      return {};
    }

    if (age) {
      const result = getEmployeeAgeWarning({ age, isAgeHidden: isBlockDemographicFiltersInSchedulingEnabled });
      return {
        isUnder18: result.isUnder18,
        isWarning: result.isWarning
      };
    }

    if (userIsUnder18 || userIsWarning) {
      return {
        isUnder18: userIsUnder18,
        isWarning: userIsWarning
      };
    }

    return {};
  }, [age, isBlockDemographicFiltersInSchedulingEnabled, userIsUnder18, userIsWarning]);

  const listTypeInfoSlug = useMemo(() => {
    switch (listType) {
      // PREFERRED means the shift sits entirely inside the preferred hours — nothing to flag, so
      // no label. A partial preference does say something the user acts on: short here, spelled
      // out in full behind the explanation mark.
      case LIST_TYPE_ENUM.PARTIAL_PREFERRED:
        return 'prefer_to_be_in_shift_short';

      case LIST_TYPE_ENUM.NOT_PREFERRED:
        return 'prefer_not_to_be_in_shift';

      case LIST_TYPE_ENUM.NOT_AVAILABLE_GA:
        return 'not_available_in_general';

      default:
        break;
    }
  }, [listType]);

  const timeAgoRenderer = useMemo(() => {
    const localMomentCreatedAt = moment.utc(createdAt, API_DATE_TIME_FORMAT).local();

    return createdAt
      ? `${createdAtPrefix} ${ago(createdAt, t)} (${localMomentCreatedAt.format(
          dateFormat
        )} ${localMomentCreatedAt.format(timeFormat)})`
      : '';
  }, [createdAt, createdAtPrefix, dateFormat, t, timeFormat]);

  const showOneLineInfo = unbooked || (rejected && showRejected);

  // replaced individual flag calculations with util
  const { isLeaf, isRookie, isCrown } = getUserCardStatsFlags({
    stats,
    rankingBadges,
    isExternal,
    isProjectInfoLoading,
    isDummyEmployeeSlot
  });

  const formattedLevel = useMemo(() => {
    if (isSeniorityModelPools(account?.config || {}) && level) {
      return formatLevelOption({ t, level });
    }

    return null;
  }, [account?.config, level, t]);

  const seniorityRankValue = useMemo(() => {
    if (isSeniorityModelRankingBased(account?.config || {}) && ranking != null) {
      return ranking;
    }

    return null;
  }, [account?.config, ranking]);

  const formattedGender = useMemo(() => {
    if (gender && !isBlockDemographicFiltersInSchedulingEnabled) {
      return genderInitial(t, gender);
    }

    return null;
  }, [gender, isBlockDemographicFiltersInSchedulingEnabled, t]);

  return (
    <Wrapper
      onClick={onClick}
      className={className}
      ref={ref}
      $listType={shouldRenderWarningBorder ? 3 : listType}
      data-testid="user-card"
      data-employee-name={displayName}>
      {applicableActions}

      <Avatar
        tooltipBody={timeAgoRenderer}
        onClick={(e) => {
          e.stopPropagation();
          openEmployeeCard?.(e);
        }}
        firstName={user?.firstName}
        lastName={user?.lastName}
        image={user?.image}
        isPending={user?.isPending}
        tooltip={showCreatedAt}
        employeeCompliance={employeeCompliance}
        date={date}
        showOnAvatarData={
          seniorityRankValue == null ? undefined : <SeniorityRankBadge>{seniorityRankValue}</SeniorityRankBadge>
        }
      />

      <Content>
        <Text>
          <AgeWarning isUnder18={isUnder18} isWarning={isWarning} />

          <EmployeeStatsPopover
            name={displayName}
            stats={stats}
            isLeaf={isLeaf}
            isRookie={isRookie}
            isCrown={isCrown}
            isExternal={isExternal}
            trigger={
              <NameTooltip
                data-testid="employee-name"
                onClick={(e) => {
                  e.stopPropagation();
                  openEmployeeCard?.(e);
                }}>
                {displayName}
              </NameTooltip>
            }
          />

          {!!inlineDataTop &&
            (inlineDataTop || []).map((data, index) => (
              <InlineTop key={index}>
                <React.Fragment key={index}>{data}</React.Fragment>
              </InlineTop>
            ))}
          {clientEmployeeStatus === 0 && <StarIcon />}
          {showOneLineInfo && (
            <InfoOneLine>
              {`(${unbooked ? t('unbooked') : ''}${unbooked && rejected && showRejected ? ', ' : ''}${
                rejected && showRejected ? t('rejected') : ''
              })`}
            </InfoOneLine>
          )}
          <Inline>
            {!!inlineActions &&
              (inlineActions || []).map((action, index) => <React.Fragment key={index}>{action}</React.Fragment>)}
          </Inline>
        </Text>

        <Info>
          <MatchArea>
            <EmployeeStatsPopover
              name={displayName}
              stats={stats}
              isLeaf={isLeaf}
              isRookie={isRookie}
              isCrown={isCrown}
              isExternal={isExternal}
              withMargin
              isDisabled={isExternal && typeof stats?.client?.isPreferred !== 'boolean'}
              trigger={
                <MatchPillWrapper>
                  <MatchPill
                    match={stats?.chosenMatch || 0}
                    isLeaf={isLeaf}
                    isRookie={isRookie}
                    isCrown={isCrown}
                    isExternal={isExternal}
                    isClientPreferred={isPreferredStatus}
                    text={isLeaf ? t('new') : isRookie ? t('earlyScore') : ''}
                    className="user-card"
                  />
                </MatchPillWrapper>
              }
            />

            {canAccessSchedulingAgent && agentAction && (
              <Tooltip
                body={agentAction === 'asked' ? t('schedulingAgentSentByAgent') : t('schedulingAgentBookedByAgent')}>
                <AiIndicator data-testid="ai-indicator" />
              </Tooltip>
            )}
          </MatchArea>

          {age && (
            <Tooltip body={t('age')}>
              <AgeText $showWarning={age < LEGAL_AGE}>{age}</AgeText>
            </Tooltip>
          )}
          {clientApproved && (
            <div>
              <Tooltip body={clientApproved.body}>
                <ApproveIcon />
              </Tooltip>
            </div>
          )}
          {formattedGender && (
            <Tooltip body={t(gender)}>
              <div>{formattedGender}</div>
            </Tooltip>
          )}

          {formattedLevel && <div>{formattedLevel}</div>}

          {scheduleNote && <TextWithTooltip>{scheduleNote}</TextWithTooltip>}
          {info?.hours > 0 && (
            <Tooltip
              body={[
                t('total_booked_hours'),
                <Trans key="weeklyOvertimeStartsAfterHours" i18nKey="weeklyOvertimeStartsAfterHours">
                  <span>{{ starts: account?.payrollConfig?.weeklyOvertimeStart || 0 }}</span>
                </Trans>
              ]}>
              <Hours $isMarked={info.hours > account?.payrollConfig?.weeklyOvertimeStart}>
                {formattingMap.number(info.hours)}
              </Hours>
            </Tooltip>
          )}
          {!!inlineData && (inlineData || []).map((data, index) => <React.Fragment key={index}>{data}</React.Fragment>)}
          {hasWarnings && <Warnings>{warnings}</Warnings>}
          {listTypeInfoSlug && <ListTypeInfo>{t(listTypeInfoSlug)}</ListTypeInfo>}
        </Info>
      </Content>

      <Actions>{actions}</Actions>
    </Wrapper>
  );
};

export default React.forwardRef(UserCard);
